import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Users, Settings, Shield, Building2, CreditCard, 
  HelpCircle, LogOut, ChevronRight
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { permissions } from '@/lib/mockAuth';

export const ProfileMenu = ({ isMobile = false }) => {
  const navigate = useNavigate();
  const { user, activeOrg, userRole, logout, getUserOrganizations } = useAuth();
  const [open, setOpen] = useState(false);
  const userOrgs = getUserOrganizations();

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'org_admin': return 'bg-primary text-primary-foreground';
      case 'admin': return 'bg-blue-600 text-white';
      case 'planner': return 'bg-accent text-accent-foreground';
      case 'client': return 'bg-secondary text-secondary-foreground';
      case 'read_only_client': return 'bg-secondary text-secondary-foreground'; // Legacy support
      default: return 'bg-muted';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'org_admin': return 'Org Admin';
      case 'admin': return 'Admin';
      case 'planner': return 'Planner';
      case 'client': return 'Client';
      case 'read_only_client': return 'Client (Read-only)'; // Legacy support
      default: return role;
    }
  };

  const canManageTeam = permissions.canManageMembers(userRole);
  const canManageOrg = permissions.canEditOrganization(userRole);
  const hasMultipleOrgs = userOrgs.length > 1;

  const handleNavigate = (path) => {
    navigate(path);
    setOpen(false);
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
  };

  const menuItems = [
    {
      label: 'Edit Profile',
      icon: User,
      onClick: () => handleNavigate('/profile'),
      show: true
    },
    {
      label: 'Team Members',
      icon: Users,
      onClick: () => handleNavigate('/organization/settings'),
      show: canManageTeam,
      adminOnly: true
    },
    {
      label: 'My Subscription',
      icon: CreditCard,
      onClick: () => handleNavigate('/account-billing'),
      show: canManageOrg,
      adminOnly: true
    },
    {
      label: 'Help & Support',
      icon: HelpCircle,
      onClick: () => handleNavigate('/help'),
      show: true
    },
    {
      label: 'Switch Organization',
      icon: Building2,
      onClick: () => handleNavigate('/switch-organization'),
      show: hasMultipleOrgs
    }
  ];

  const ProfileAvatar = () => (
    <Avatar className="h-9 w-9 cursor-pointer hover:ring-2 hover:ring-primary transition-all">
      <AvatarImage src={user?.avatar} alt={user?.name} />
      <AvatarFallback className="gradient-primary text-primary-foreground font-semibold">
        {getInitials(user?.name)}
      </AvatarFallback>
    </Avatar>
  );

  const MenuContent = ({ onItemClick }) => (
    <div className="py-2">
      {/* User Info Header */}
      <div className="px-4 py-3 border-b">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <AvatarImage src={user?.avatar} alt={user?.name} />
            <AvatarFallback className="gradient-primary text-primary-foreground font-semibold text-lg">
              {getInitials(user?.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{user?.name}</p>
            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            <Badge className={`${getRoleBadgeColor(userRole)} mt-1 text-xs`}>
              {getRoleLabel(userRole)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="py-2">
        {menuItems.filter(item => item.show).map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              onClick={() => {
                item.onClick();
                if (onItemClick) onItemClick();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors text-left"
            >
              <Icon className="h-5 w-5 text-muted-foreground" />
              <span className="flex-1">{item.label}</span>
              {item.adminOnly && (
                <Badge variant="outline" className="text-xs">Admin</Badge>
              )}
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          );
        })}
      </div>

      {/* Logout */}
      <div className="border-t pt-2">
        <button
          onClick={() => {
            handleLogout();
            if (onItemClick) onItemClick();
          }}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-destructive/10 text-destructive transition-colors text-left"
        >
          <LogOut className="h-5 w-5" />
          <span className="flex-1 font-medium">Logout</span>
        </button>
      </div>
    </div>
  );

  // Mobile: Bottom Sheet
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button className="focus:outline-none">
            <ProfileAvatar />
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Account Menu</SheetTitle>
          </SheetHeader>
          <div className="h-full overflow-y-auto">
            <MenuContent onItemClick={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="focus:outline-none">
          <ProfileAvatar />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <MenuContent />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileMenu;